import { useCallback, useMemo, useState } from 'react';

// mui
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';

// types
import { OrganiseMember, RecurrenceFrequency } from '@karehero/models';

// components
import { Icon } from '@/components/atoms/Icon/Icon';

// types
import AssigneeSelect from '@/pwa/molecules/AssigneeSelect/AssigneeSelect';
import { RecurrenceControls } from '@/pwa/molecules/RecurrenceControls/RecurrenceControls';

export type EntryDialogMember = OrganiseMember;

export interface EntryDialogCalendar {
  calendarId: string;
  label: string;
  members: EntryDialogMember[];
}

const toLocalDate = (iso: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60000);
  return local.toISOString().slice(0, 10);
};

const toLocalTime = (iso: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60000);
  return local.toISOString().slice(11, 16);
};

const combineDateAndTime = (date: string, time: string): string | null => {
  if (!date) return null;
  const datetime = time ? `${date}T${time}` : `${date}T00:00`;
  return new Date(datetime).toISOString();
};

export interface EntryState {
  calendarId: string;
  entryId?: string;
  title: string;
  assigneeAccountId: string | null;
  startTime: string | null;
  endTime: string | null;
  isRecurring: boolean;
  recurrenceFrequency: RecurrenceFrequency;
  recurrenceInterval: number;
  isRepeatingTaskEntry: boolean;
  isUpdatingRepeatingTask: boolean;
  recurringEventId?: string | null;
  occurrenceDate?: string | null;
}

export const isFormValid = (draftEntry: EntryState) => {
  const { calendarId, title, startTime, isRecurring, recurrenceInterval } =
    draftEntry;

  return !(
    !title.trim() ||
    !calendarId ||
    (isRecurring && !startTime) ||
    (isRecurring && (!recurrenceInterval || recurrenceInterval < 1))
  );
};

interface OrganiserEntryFormProps {
  draftEntry: EntryState;
  setDraftEntry: any;
  mode: 'create' | 'edit';
  calendars: EntryDialogCalendar[];
}

export const OrganiserEntryForm = ({
  draftEntry,
  setDraftEntry,
  mode,
  calendars,
}: OrganiserEntryFormProps) => {
  const calendarId = draftEntry.calendarId;
  const title = draftEntry.title;
  const assigneeAccountId = draftEntry.assigneeAccountId;
  const startTime = draftEntry.startTime;
  const isRecurring = draftEntry.isRecurring;
  const recurrenceFrequency = draftEntry.recurrenceFrequency;
  const recurrenceInterval = draftEntry.recurrenceInterval;
  const isRecurringEntry = draftEntry.isRepeatingTaskEntry;
  const isEditingSeries = draftEntry.isUpdatingRepeatingTask;

  const [hasExplicitTime, setHasExplicitTime] = useState(
    () => !!startTime && toLocalTime(startTime) !== '00:00',
  );

  const members = useMemo(
    () =>
      calendars?.find((calendar) => calendar.calendarId === calendarId)
        ?.members || [],
    [calendars, calendarId],
  );

  const onCalendarChange = useCallback(
    (calendarId: string) => setDraftEntry((s: any) => ({ ...s, calendarId })),
    [],
  );

  const onTitleChange = useCallback(
    (title: string) => setDraftEntry((s: any) => ({ ...s, title })),
    [],
  );

  const onAssigneeChange = (assigneeAccountId: string | null) =>
    setDraftEntry((s: any) => ({ ...s, assigneeAccountId }));

  const onStartTimeChange = (startTime: string | null) =>
    setDraftEntry((s: any) => ({ ...s, startTime }));

  const onRecurrenceFrequencyChange = (
    recurrenceFrequency: RecurrenceFrequency,
  ) => setDraftEntry((s: any) => ({ ...s, recurrenceFrequency }));

  const onRecurrenceIntervalChange = (recurrenceInterval: number) =>
    setDraftEntry((s: any) => ({ ...s, recurrenceInterval }));

  const onEditingSeriesChange = (isUpdatingRepeatingTask: boolean) =>
    setDraftEntry((s: any) => ({ ...s, isUpdatingRepeatingTask }));

  const onRecurringChange = (isRecurring: boolean) =>
    setDraftEntry((s: any) => ({ ...s, isRecurring }));

  return (
    <Stack
      spacing={3}
      sx={{
        mt: 1,
      }}
    >
      {mode === 'create' && onRecurringChange && (
        <FormControlLabel
          control={
            <Checkbox
              checked={isRecurring}
              onChange={(e) => onRecurringChange(e.target.checked)}
            />
          }
          label='Select to create a recurring action'
        />
      )}

      {calendars.length >= 1 && (
        <FormControl fullWidth disabled={mode === 'edit'}>
          <InputLabel shrink>Care Circle</InputLabel>
          <Select
            notched
            value={calendarId}
            onChange={(e) => {
              onCalendarChange(e.target.value);
              onAssigneeChange(null);
            }}
            label='Care Circle'
          >
            {calendars.map((c: any) => (
              <MenuItem key={c.calendarId} value={c.calendarId}>
                {c.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <TextField
        autoFocus
        label='Title'
        value={title}
        onChange={(e) => onTitleChange(e.target.value)}
        fullWidth
        required
        slotProps={{ inputLabel: { shrink: true } }}
      />

      <AssigneeSelect
        assigneeAccountId={assigneeAccountId}
        onAssigneeChange={onAssigneeChange}
        members={members}
      />

      <TextField
        label={isRecurring ? 'Starting from' : 'Date'}
        type='date'
        value={toLocalDate(startTime)}
        onChange={(e) =>
          onStartTimeChange(
            e.target.value
              ? combineDateAndTime(e.target.value, toLocalTime(startTime))
              : null,
          )
        }
        fullWidth
        required={isRecurring}
        slotProps={{ inputLabel: { shrink: true } }}
      />
      <TextField
        label='Time'
        type='time'
        value={hasExplicitTime ? toLocalTime(startTime) : ''}
        onChange={(e) => {
          if (e.target.value) {
            setHasExplicitTime(true);
            onStartTimeChange(
              toLocalDate(startTime)
                ? combineDateAndTime(toLocalDate(startTime), e.target.value)
                : null,
            );
          }
        }}
        fullWidth
        disabled={!startTime}
        slotProps={{
          inputLabel: { shrink: true },
          input: {
            sx: hasExplicitTime
              ? {
                  '& input::-webkit-calendar-picker-indicator': {
                    ml: 0.5,
                  },
                }
              : undefined,
            endAdornment: hasExplicitTime ? (
              <IconButton
                size='small'
                onClick={() => {
                  setHasExplicitTime(false);
                  onStartTimeChange(
                    combineDateAndTime(toLocalDate(startTime), ''),
                  );
                }}
                sx={{ position: 'absolute', right: 32 }}
              >
                <Icon icon='xmark' fontSize={14} />
              </IconButton>
            ) : undefined,
          },
        }}
      />

      {mode === 'create' && isRecurring && (
        <RecurrenceControls
          frequency={recurrenceFrequency}
          onFrequencyChange={onRecurrenceFrequencyChange}
          interval={recurrenceInterval}
          onIntervalChange={onRecurrenceIntervalChange}
        />
      )}

      {mode === 'edit' && isRecurringEntry && onEditingSeriesChange && (
        <>
          <FormControlLabel
            control={
              <Checkbox
                checked={isEditingSeries}
                onChange={(e) => onEditingSeriesChange(e.target.checked)}
              />
            }
            label='Update all repeating actions'
          />
          <RecurrenceControls
            frequency={recurrenceFrequency}
            onFrequencyChange={onRecurrenceFrequencyChange}
            interval={recurrenceInterval}
            onIntervalChange={onRecurrenceIntervalChange}
            isDisabled={!isEditingSeries}
          />
        </>
      )}
    </Stack>
  );
};

OrganiserEntryForm.displayName = 'OrganiserEntryForm';
export default OrganiserEntryForm;
